import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import * as trainingQueries from '../../../api/Training/queries';
import {
  prepareGetAssignedTrainingQueryForTM,
  prepareGetAssignedTrainingQueryForCSA,
} from '../../../api/Training';
import { parseTrainingData } from '../TrainingList/parser';
import { Profile } from '../../../Types/Profile';
import { TrainingRecord } from '../TrainingList/types';
import { TrainingViewMode } from '../types/constants';

interface UseAssignedTrainingResult {
  trainings: TrainingRecord[];
  loading: boolean;
  error: boolean;
}

export const useAssignedTraining = (
  profile: Profile,
  cscEntityPrefix: string,
  userType: TrainingViewMode = TrainingViewMode.TM
): UseAssignedTrainingResult => {
  const queryTrainingData = useMemo(() => {
    if (userType === TrainingViewMode.CSA) {
      return prepareGetAssignedTrainingQueryForCSA(profile, cscEntityPrefix);
    }
    return prepareGetAssignedTrainingQueryForTM(profile, cscEntityPrefix);
  }, [profile, cscEntityPrefix, userType]);

  const trainingProfiles = useMemo(() => {
    return Array.from(
      new Map(
        [...profile.directReports, ...profile.sharedProfileCoachees].map(
          user => [user.userID, user]
        )
      ).values()
    );
  }, [profile.directReports, profile.sharedProfileCoachees]);

  const { loading, error, data } = useQuery(
    trainingQueries.getAssignedTrainings,
    {
      variables: queryTrainingData,
      fetchPolicy: 'cache-and-network',
    }
  );

  const trainings = useMemo(() => {
    if (!data?.getAssignedTrainings) {
      return [];
    }
    return parseTrainingData(data.getAssignedTrainings, trainingProfiles);
  }, [data, trainingProfiles]);

  return {
    trainings,
    loading,
    error: !!error,
  };
};

export default useAssignedTraining;
